import { Text } from '@/components/ui/text'
import { api } from '@api'
import { createQuery } from '@tanstack/solid-query'
import { For, Match, Switch, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Flex } from 'styled-system/jsx'
import { SetupImageCarousel } from '@components/SetupImageCarousel'
import Visualisation from '../../../Visualisation'
import Live from '../../Chart/Live'
import { useSingle } from '../../state'

type ActiveView = 'live' | 'wireframe' | number

const TV_LOGO_BG = '#ffffff'

export default function Gallery() {
  const { setupQuery } = useService(useSingle)()
  const [openImage, setOpenImage] = createSignal<number>()

  const rawImages = () => [
    { title: 'Macro', src: setupQuery.data?.image_macro },
    { title: 'Trend', src: setupQuery.data?.image_trend },
    { title: 'Pattern', src: setupQuery.data?.image_pattern },
    { title: 'Trigger', src: setupQuery.data?.image_trigger },
    { title: 'Time Stop', src: setupQuery.data?.image_time_stop },
    { title: 'Volume by price', src: setupQuery.data?.image_volume_by_price },
  ].filter((image): image is { title: string; src: string } => typeof image.src === 'string' && image.src.length > 0)

  const imagesQuery = createQuery(() => ({
    queryKey: ['setup-images-batch-mobile', setupQuery.data?.id],
    queryFn: async () => {
      const images = rawImages()
      const supabasePaths = images.map((img) => img.src).filter((src) => !src.startsWith('http'))
      const signedUrlsMap: Record<string, string> = {}

      if (supabasePaths.length > 0) {
        const { data, error } = await api().storage
          .from('setup-images')
          .createSignedUrls(supabasePaths, 3600)

        if (!error && data) {
          data.forEach((item) => {
            if (item.signedUrl && item.path) signedUrlsMap[item.path] = item.signedUrl
          })
        }
      }

      return images
        .map((img) => (img.src.startsWith('http')
          ? { title: img.title, src: img.src }
          : { title: img.title, src: signedUrlsMap[img.src] || '' }))
        .filter((img) => img.src !== '')
    },
    enabled: !!setupQuery.data?.id && rawImages().length > 0,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  }))

  const resolvedImages = () => imagesQuery.data || []

  const CORE_TITLES = ['Macro', 'Trend', 'Pattern', 'Trigger']
  const coreImages = () => resolvedImages()
    .map((image, index) => ({ image, index }))
    .filter(({ image }) => CORE_TITLES.includes(image.title))
  const extraImages = () => resolvedImages()
    .map((image, index) => ({ image, index }))
    .filter(({ image }) => !CORE_TITLES.includes(image.title))

  const defaultViewIndex = () => {
    const patternIndex = resolvedImages().findIndex((img) => img.title === 'Pattern')
    return patternIndex >= 0 ? patternIndex : 0
  }
  const [activeView, setActiveView] = createSignal<ActiveView>(defaultViewIndex())

  const currentTitle = () => {
    const v = activeView()
    if (v === 'live') return 'Live TV Chart'
    if (v === 'wireframe') return 'Wireframe'
    return resolvedImages()[v]?.title || ''
  }

  const thumb = (label: string, isActive: () => boolean, onClick: () => void, imgSrc?: string, isLive?: boolean) => (
    <Box
      as="button"
      onClick={onClick}
      pos="relative"
      flexShrink={0}
      w="104px"
      aspectRatio="16/9"
      borderRadius="9px"
      overflow="hidden"
      style={{ border: `1.5px solid ${isActive() ? '#12b886' : 'rgba(20, 22, 30, 0.09)'}` }}
      bg="#f1f3f6"
    >
      {imgSrc
        ? (
          <img
            src={imgSrc}
            alt={label}
            style={{
              width: '100%',
              height: '100%',
              'object-fit': isLive ? 'contain' : 'cover',
              background: isLive ? TV_LOGO_BG : undefined,
              padding: isLive ? '9px' : undefined,
            }}
          />
        )
        : null}
      <Text
        pos="absolute"
        left="6px"
        bottom="5px"
        fontSize="9.5px"
        fontWeight="700"
        color="#ffffff"
        borderRadius="6px"
        px="7px"
        py="2px"
        whiteSpace="nowrap"
        style={{ background: isLive ? '#12b886' : '#646464' }}
      >
        {label}
      </Text>
    </Box>
  )

  return (
    <>
      <SetupImageCarousel
        images={resolvedImages()}
        openImage={openImage}
        setIsOpen={setOpenImage}
      />

      <Box
        pos="relative"
        aspectRatio="16/10"
        bg="#f1f3f6"
        overflow="hidden"
        onClick={() => {
          const v = activeView()
          if (typeof v === 'number') setOpenImage(v)
        }}
      >
        <Switch
          fallback={
            resolvedImages()[activeView() as number]
              ? (
                <img
                  src={resolvedImages()[activeView() as number].src}
                  alt={currentTitle()}
                  style={{ width: '100%', height: '100%', 'object-fit': 'cover' }}
                />
              )
              : null
          }
        >
          <Match when={activeView() === 'live'}>
            <Live />
          </Match>
          <Match when={activeView() === 'wireframe'}>
            <Visualisation height="100%" setup={setupQuery.data!} />
          </Match>
        </Switch>

        <Text
          pos="absolute"
          left="50%"
          bottom="10px"
          fontSize="11.5px"
          fontWeight="600"
          color="#14161b"
          borderRadius="999px"
          px="14px"
          py="5px"
          bg="rgba(255, 255, 255, 0.95)"
          border="1px solid rgba(20, 22, 30, 0.15)"
          style={{ transform: 'translateX(-50%)' }}
        >
          {currentTitle()}
        </Text>
      </Box>

      <Flex
        gap="9px"
        px="14px"
        py="11px"
        overflowX="auto"
        class={css({
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        })}
      >
        {thumb('Live TV Chart', () => activeView() === 'live', () => setActiveView('live'), undefined, true)}
        <For each={coreImages()}>
          {(item) => thumb(item.image.title, () => activeView() === item.index, () => setActiveView(item.index), item.image.src)}
        </For>
        {thumb('Wireframe', () => activeView() === 'wireframe', () => setActiveView('wireframe'))}
        <For each={extraImages()}>
          {(item) => thumb(item.image.title, () => activeView() === item.index, () => setActiveView(item.index), item.image.src)}
        </For>
      </Flex>
    </>
  )
}
